import classNames from 'classnames/bind';
import TrackingEvent from 'constants/trackingEvents';
import { useWindowSize } from 'hooks/useWindowSize';
import MenuTab from 'koba/components/MobileMenu/components/MenuTab';
import { useTranslation } from 'react-i18next';
import { getFollowingUrl, getSettingsUrl, routes } from 'utils/routerHelpers';
import { hasModeratorBadge, isAdmin } from 'utils/userHelpers';

import React from 'react';

import { Button } from '@thinkific/chalk/Button';
import { Text } from '@thinkific/chalk/Text';
import { atomic } from '@thinkific/chalk/atomic';
import { BookmarkSimple, Gear, SignOut, SquaresFour, User, X } from '@thinkific/chalk/react-icons';
import { MenuItemWithSymbols } from '@thinkific/chalk/recipes/MenuItemWithSymbols';

import { useCommunityContext } from 'components/CommunityContext';
import UserAvatar from 'components/UserAvatar';
import ModeratorBadge from 'components/new/ModeratorBadge';

import * as css from './UserAvatarMenu.css';
import styles from './UserAvatarMenu.module.scss';

const cx = classNames.bind(styles);
interface UserAvatarMenuProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  user: {
    fullName: string;
    avatarUrl: string;
    id: string;
    isModerator: boolean;
  };
}

const UserAvatarMenu = ({ isOpen, onClose, communityId, user }: UserAvatarMenuProps) => {
  const { t } = useTranslation();
  const { trackEvent, currentUser } = useCommunityContext();
  const { isMobile } = useWindowSize();
  const showModeratorBadge = hasModeratorBadge(user);
  const RoleBadge = () => {
    const label = showModeratorBadge ? t('Moderator', 'Moderator') : t('common-Member', 'Member');
    return (
      <div className={cx('side-menu__moderator-badge__container')}>
        <ModeratorBadge>{label as string}</ModeratorBadge>
      </div>
    );
  };

  const goTo = (route: string, event?: TrackingEvent) => {
    if (event) trackEvent(event, {});
    window.location.href = route;
  };

  const AdminItems = () => (
    <>
      <MenuItemWithSymbols
        data-qa="menu-options-community-settings__btn"
        href={getSettingsUrl(communityId)}
        label={t('components-currentUserDropdown-settings_button', 'Community settings')}
        leadSymbol={<Gear />}
        onClick={() => goTo(getSettingsUrl(communityId))}
      />
      <MenuItemWithSymbols
        data-qa="menu-options-all-communities__btn"
        href={routes.communities}
        label={t('components-currentUserDropdown-all_communities', 'All communities')}
        leadSymbol={<SquaresFour />}
        onClick={() => goTo(routes.communities)}
      />
    </>
  );

  const closeButton = (
    <Button
      data-qa="profile-panel__close"
      emphasis="low"
      icon={X}
      size="sm"
      hiddenLabel
      onClick={onClose}
    >
      {t('Close profile panel')}
    </Button>
  );

  const StudentItems = () => (
    <>
      <MenuItemWithSymbols
        href={routes.dashboard}
        label={t('components-currentUserDropdown-my_dashboard_button', 'My dashboard')}
        leadSymbol={<SquaresFour />}
        onClick={() => goTo(routes.dashboard)}
      />
    </>
  );

  return (
    <MenuTab
      hasHeader={false}
      isOpen={isOpen}
      menuTitle={t('Profile', 'Profile')}
      size={isMobile ? 'small' : 'medium'}
      slideTo="left"
      hasOverlay
      onClose={onClose}
    >
      <div
        aria-label={t(
          'components-userAvatarMenu-aria-label',
          'User Avatar Menu, a list of options pertaining to the user and community'
        )}
        className={cx('side-menu')}
        data-qa="side-menu__wrapper"
        role="dialog"
      >
        <div className={css.profileHeader}>
          <Text as="h3" variant="display.sm">
            {t('Profile')}
          </Text>
          {closeButton}
        </div>
        <div className={cx('side-menu__content')}>
          <div className={cx('side-menu__header')}>
            <UserAvatar
              avatarUrl={user.avatarUrl}
              className={cx('side-menu__avatar')}
              size="md"
              userId={user.id}
              username={user.fullName}
            />
          </div>
          <div className={cx('side-menu__info')}>
            {user.fullName && (
              <Text as="h4" variant="display.sm">
                {user.fullName}
              </Text>
            )}
            <div className={cx('side-menu__label')}>
              <RoleBadge />
            </div>
          </div>
        </div>

        <section className={cx('side-menu__content')}>
          <div className={cx('side-menu__buttons')}>
            <MenuItemWithSymbols
              href={getFollowingUrl(communityId)}
              label={t('components-currentUserDropdown-following', 'Following')}
              leadSymbol={<BookmarkSimple />}
              onClick={() => goTo(getFollowingUrl(communityId))}
            />
            {isAdmin(currentUser) && <AdminItems />}
            {!isAdmin(currentUser) && <StudentItems />}
            <MenuItemWithSymbols
              href={routes.myAccount}
              label={t('components-currentUserDropdown-edit_profile_button', 'Edit profile')}
              leadSymbol={<User />}
              onClick={() => goTo(routes.myAccount, TrackingEvent.MY_ACCOUNT)}
            />
            <MenuItemWithSymbols
              className={atomic({ color: 'action.destructive.dark' })}
              href={routes.signOut}
              label={t('components-currentUserDropdown-log_out_button', 'Log out')}
              leadSymbol={<SignOut />}
              variant="destructive"
              onClick={() => {
                localStorage.removeItem('draftPosts');
                goTo(routes.signOut);
              }}
            />
          </div>
        </section>
      </div>
    </MenuTab>
  );
};

export default UserAvatarMenu;
